import { Box, Button, Flex, Skeleton, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { api } from 'views/Social/api'
import { storeRef } from 'views/Social/refstore'
import styled, { css } from 'styled-components'
import useSWR from 'swr'
import PoolTradePanel from 'views/PoolTrade/PoolTradePanel'
import { timeframeFor } from 'views/TokenPages/candles'
import ChartCard from 'views/TokenPages/components/ChartCard'
import SectionBoundary from 'views/TokenPages/components/SectionBoundary'
import type { ChartMark } from 'views/TokenPages/components/PriceChart'
import TradeHistory from 'views/TokenPages/components/TradeHistory'
import { useOverview } from 'views/TokenPages/hooks'
import type { TfKey } from 'views/TokenPages/types'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { Icon } from 'views/TokenPages/icons'
import DetailsCard from './components/DetailsCard'
import Fold from './components/Fold'
import Leaderboard, { BoardRow } from './components/Leaderboard'
import LockBadge, { LockPill } from './components/LockBadge'
import RulesAck, { useRulesAck } from './components/RulesAck'
import RulesSummary, { RulesCard } from './components/RulesSummary'
import Shell, { ActionButton, Mono, Panel, useV3Ready } from './components/Shell'
import TokenAmount from './components/TokenAmount'
import CompetitionShare from './components/CompetitionShare'
import { EnterButton, TRADE_ANCHOR, enterLabel, goToTrade } from './components/EnterCta'
import { DexLogo } from './pool-search/PoolSearch'
import { useV3Tx } from './components/useV3Tx'
import { competitionV3Abi } from './abi'
import { TYPES, fetcher, short } from './config'
import { useContractBinding } from './contract'
import { usePoolPair, useTokenMeta } from './hooks'
import { timeCaption, tradingLayout, yourStatus } from './layout'
import { useProfiles } from './identity/useProfiles'
import { whenBoth } from './rules'
import { tokenAmountText } from './components/tokenText'
import { Bar, Card, Countdown, Display, Eyebrow, Icon3d, Num, PhasePill, Pill, fmtDate, useNow } from './ui'

const Head = styled(Box)`
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: start;
  }
`
/*
 * Chart, trade, rules, history: one column on a phone in that order. From desktop (layout.ts, Bez 2026-10-03):
 *  - live ("side"): the tall trade panel beside the chart, history and rules under the chart;
 *  - any other phase ("stack"): the chart across the full width, then the trade card and the rules card as equal-height
 *    twins, then the history across the full width. No column is ever left empty.
 */
const Trading = styled(Box)<{ $side: boolean }>`
  display: grid;
  gap: 16px;
  margin-top: 16px;
  grid-template-columns: minmax(0, 1fr);
  > * { min-width: 0; }
  ${({ theme }) => theme.mediaQueries.lg} {
    ${({ $side }) =>
      $side
        ? css`
            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
            align-items: start;
            > .chart { grid-column: 1; grid-row: 1; }
            > .trade { grid-column: 2; grid-row: 1 / span 3; } /* not sticky: the full panel is taller than a laptop screen */
            > .history { grid-column: 1; grid-row: 2; }
            > .rules { grid-column: 1; grid-row: 3; }
          `
        : css`
            grid-template-columns: repeat(2, minmax(0, 1fr));
            align-items: stretch;
            > .chart { grid-column: 1 / -1; grid-row: 1; }
            > .trade { grid-column: 1; grid-row: 2; display: flex; }
            > .rules { grid-column: 2; grid-row: 2; display: flex; }
            > .trade > *, > .rules > * { flex: 1; min-width: 0; }
            > .history { grid-column: 1 / -1; grid-row: 3; }
          `}
  }
`
/* the new sections keep the night palette whatever the site theme is (the chart, the trade panel and the history are drawn on it); display: contents adds no box */
const Night = styled.div`
  display: contents;
`
/* the page's first card: a night-lit header, the prize as a gold hero with a 3D gem, time as a glowing bar (four.meme's bonding bar, our moon colours) */
const Hero = styled(Card)`
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(60% 90% at 100% 0%, rgba(246, 196, 83, 0.14), transparent 70%),
    radial-gradient(50% 80% at 0% 0%, rgba(127, 212, 232, 0.12), transparent 70%),
    ${({ theme }) => theme.colors.backgroundAlt};
`
const PrizeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 20px;
  border: 1px solid rgba(246, 196, 83, 0.35);
  background: linear-gradient(135deg, rgba(246, 196, 83, 0.16), rgba(246, 196, 83, 0.03));
  box-shadow: 0 10px 30px rgba(246, 196, 83, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.12);
`
/* the countdown and its date live in the header's left column, under the pills, not in a band of their own (Bez: "the Starts in bar is mostly empty on 1440") */
const TimeLine = styled(Flex)`
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
  margin-top: 12px;
`
const Hairline = styled(Box)`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(180, 200, 255, 0.12);
`
const Steps = styled(Box)`
  display: grid;
  gap: 4px 16px;
  margin-top: 8px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`
const Step = styled(Flex)<{ $done: boolean; $now: boolean }>`
  gap: 12px;
  align-items: flex-start;
  padding-bottom: 8px;
  &::before {
    content: '';
    flex: none;
    width: 14px;
    height: 14px;
    margin-top: 3px;
    border-radius: 50%;
    border: 2px solid ${({ theme, $done, $now }) => ($now ? theme.colors.success : $done ? theme.colors.primary : theme.colors.cardBorder)};
    background: ${({ theme, $done }) => ($done ? theme.colors.primary : 'transparent')};
  }
`
type Live = { state?: string; headBlock?: number; indexedThroughBlock?: number; lagSeconds?: number; ageSeconds?: number; stale?: boolean }
const fmtLag = (sec: number) => (sec < 90 ? `${Math.max(0, Math.round(sec))} s` : `${Math.round(sec / 60)} min`)
/** What the board is doing right now: live (block, lag), delayed (worker behind or silent), finalizing (ended, being verified), or waiting for the chain. */
function LiveStatus({ kind, live, ended }: { kind?: string; live?: Live | null; ended: boolean }) {
  if (kind === 'final') return null
  if (kind === 'candidate') return <Text data-testid="v3-live" data-state="final_pending" fontSize="13px" color="warning">Final result published · waiting for the on-chain settlement</Text>
  if (kind === 'provisional' && live) {
    const behind = (live.lagSeconds ?? 0) + (live.ageSeconds ?? 0)
    if (live.stale) return <Text data-testid="v3-live" data-state="stale" fontSize="13px" color="warning">Delayed · counted up to block {live.indexedThroughBlock} · last update {fmtLag(live.ageSeconds ?? behind)} ago</Text>
    if (live.state === 'finalizing' || ended) return <Text data-testid="v3-live" data-state="finalizing" fontSize="13px" color="warning">Finalizing · counted up to block {live.indexedThroughBlock} · the result is being verified</Text>
    return <Text data-testid="v3-live" data-state="live" fontSize="13px" color="success">● Live · block {live.indexedThroughBlock} · lag {fmtLag(behind)}</Text>
  }
  if (ended) return <Text data-testid="v3-live" data-state="finalizing" fontSize="13px" color="warning">Finalizing · the competition has ended</Text>
  return null
}

export default function CompetitionDetailV3() {
  const { query } = useRouter()
  const id = query.id as string
  const { ready, address } = useV3Ready()
  const { send, pending } = useV3Tx()
  const now = useNow()
  const { apiUrl, address: contractAddress, base } = useContractBinding()
  const { data: c, mutate, error } = useSWR(id ? `${apiUrl}/${id}` : null, fetcher, { refreshInterval: (d: any) => (d && d.phase === 'awaiting_result' ? 10000 : 30000) })
  // The board is served from the worker's stored result every 5 s while the competition runs or waits for its result.
  const busy = c?.phase === 'live' || c?.phase === 'awaiting_result'
  const { data: lb } = useSWR(id && c ? `${apiUrl}/${id}/leaderboard` : null, fetcher, { refreshInterval: busy ? 5000 : 30000 })
  const { data: report } = useSWR(c?.report ? `${apiUrl}/${id}/report` : null, fetcher, { refreshInterval: c?.report?.kind === 'candidate' ? 10000 : 0 })
  const project = useTokenMeta(c?.projectToken, true)
  // profiles of every wallet on the board in ONE request; the rows show addresses at once and take names and pictures when (and if) it answers
  const profiles = useProfiles(lb?.rows?.map((r: { wallet: string }) => r.wallet) ?? [])
  const pair = usePoolPair(c?.pool)
  // chart, trade panel and history of exactly this competition's pool
  const overview = useOverview(c?.projectToken)
  const selPool = overview.data?.pools.find((p) => p.pool.toLowerCase() === c?.pool?.toLowerCase())
  const [userTf, setUserTf] = useState<TfKey | null>(null)
  const autoTf = useMemo(() => timeframeFor(selPool?.lastTradeAt, Math.floor(Date.now() / 1000)), [selPool?.pool, selPool?.lastTradeAt]) // eslint-disable-line react-hooks/exhaustive-deps
  const tf = userTf ?? autoTf
  const chartMarks = useMemo<ChartMark[]>(() => (c ? [{ t: c.startTime, label: 'Start', tone: 'start' }, { t: c.endTime, label: 'End', tone: 'end' }] : []), [c?.startTime, c?.endTime]) // eslint-disable-line react-hooks/exhaustive-deps
  const ack = useRulesAck(c ? { id: c.id, pool: c.pool, startTime: c.startTime, endTime: c.endTime, rulesHash: c.rulesHash } : undefined, address)
  // a shared competition link (?ref=<handle or wallet>, CompetitionShare) invites like /r/<id>: first touch, never the visitor itself
  useEffect(() => {
    const r = typeof query.ref === 'string' ? query.ref : ''
    if (!r || r.length > 64) return
    api<{ address?: string }>(`profiles/${encodeURIComponent(r)}`)
      .then((p) => p?.address && (!address || p.address.toLowerCase() !== address.toLowerCase()) && storeRef(p.address))
      .catch(() => (/^0x[0-9a-fA-F]{40}$/.test(r) && (!address || r.toLowerCase() !== address.toLowerCase()) ? storeRef(r) : undefined))
  }, [query.ref, address])
  const rewardMeta = useTokenMeta(c?.rewardToken, true)
  // a link to #trade-column (a live card's "Buy ... to enter") lands on the trade panel once the competition has rendered
  const loadedId = c?.id
  useEffect(() => {
    if (loadedId === undefined || typeof window === 'undefined' || window.location.hash !== `#${TRADE_ANCHOR}`) return undefined
    const t = setTimeout(goToTrade, 400)
    return () => clearTimeout(t)
  }, [loadedId])
  if (error) return (
    <Shell title="Competition"><Panel data-testid="v3-notfound"><Text bold>This competition does not exist on the current contract.</Text><Link href={base} style={{ textDecoration: 'underline', display: 'inline-flex', minHeight: 44, alignItems: 'center' }}>Back to all competitions</Link></Panel></Shell>
  )
  if (!c) return <Shell title="Competition"><Skeleton height="240px" /></Shell>
  const me = address?.toLowerCase()
  const mine = me && c.winners.find((w) => w.wallet === me)
  const type = TYPES.find((x) => x.key === c.type)
  const title = project.symbol ?? short(c.projectToken)
  const t = now ?? 0
  const marks = [
    { label: 'Starts', at: c.startTime, done: t >= c.startTime },
    { label: 'Ends', at: c.endTime, done: t >= c.endTime },
    { label: 'Result due', at: c.refundAfter, done: t >= c.refundAfter || c.status === 'settled' },
  ]
  const elapsed = c.endTime > c.startTime ? Math.min(100, Math.max(0, ((t - c.startTime) / (c.endTime - c.startTime)) * 100)) : 0
  const boardRows: BoardRow[] = lb?.rows ?? []
  // the full trade form shows only once the rules are accepted; until then the panel is a short gate
  const panel = c.phase === 'live' ? (ack.state === 'done' ? 'full' : 'gated') : 'closed'
  const side = tradingLayout(c.phase, panel === 'full') === 'side'
  const myRow = me ? boardRows.find((r) => r.wallet.toLowerCase() === me) : undefined
  const status = yourStatus({ phase: c.phase, wallet: address, rank: myRow?.rank ?? null, won: Boolean(mine), claimed: Boolean(mine?.claimed), ackDone: ack.state === 'done', winners: c.numberOfWinners })
  const statusColor = status.tone === 'success' ? 'success' : status.tone === 'subtle' ? 'textSubtle' : 'text'
  const showAck = c.phase === 'upcoming' && Boolean(address) && (ack.state === 'needed' || ack.state === 'signing' || ack.state === 'unavailable')
  const statusBlock = (
    <Box data-testid="v3-your-status" data-tone={status.tone}>
      <Text fontSize="13px" color="textSubtle">Your status</Text>
      <Text fontSize="14px" color={statusColor} mt="2px">{status.text}</Text>
      {showAck && <Box mt="12px"><RulesAck state={ack.state} onSign={ack.sign} error={ack.error} /></Box>}
    </Box>
  )
  const caption = timeCaption(c.phase, c.startTime, c.endTime, fmtDate)
  const rulesProps = {
    type: c.type,
    poolLabel: `${pair.dex ? pair.dex.name : 'this pool'}${pair.symbol0 && pair.symbol1 ? ` · ${pair.symbol0}/${pair.symbol1}` : ''}`,
    projectSymbol: project.symbol ?? '',
    startTime: c.startTime,
    endTime: c.endTime,
    refundAfter: c.refundAfter,
    winners: c.numberOfWinners,
    rulesHash: c.rulesHash,
  }
  return (
    <Shell>
      <Hero>
        <Head>
          <Box style={{ minWidth: 0 }}>
            <Flex alignItems="center" style={{ gap: 16 }}>
              <TokenLogo address={c.projectToken} symbol={project.symbol} size={72} ring />
              <Box style={{ minWidth: 0 }}>
                <Eyebrow>Competition #{c.id}</Eyebrow>
                <Display as="h1" bold fontSize={['28px', null, '36px']} lineHeight="1.1" style={{ overflowWrap: 'anywhere' }}>{title}</Display>
                <Flex alignItems="center" style={{ gap: 6 }} mt="2px">
                  {pair.dex && <DexLogo dex={pair.dex.name} size={16} />}
                  <Text fontSize="13px" color="textSubtle">
                    {pair.dex ? pair.dex.name : 'Pool'} {pair.symbol0 && pair.symbol1 ? `· ${pair.symbol0}/${pair.symbol1}` : ''}
                  </Text>
                </Flex>
              </Box>
            </Flex>
            <Flex mt="16px" style={{ gap: 8 }} flexWrap="wrap" alignItems="center">
              <PhasePill phase={c.phase} />
              <Pill $tone="info">{type?.short ?? c.type}</Pill>
              <LockPill lock={c.liquidityLock} />
            </Flex>
            <TimeLine data-testid="v3-timebar" data-phase={c.phase}>
              <Text bold fontSize="18px"><Countdown c={c} /></Text>
              {caption && <Text fontSize="12px" color="textSubtle">{caption}</Text>}
            </TimeLine>
          </Box>
          <Box style={{ display: 'grid', gap: 12, minWidth: 0 }} data-testid="v3-prize-col">
            <PrizeBox>
              <Icon3d name="gem_stone" size={56} />
              <Box style={{ minWidth: 0 }}>
                <Text fontSize="12px" color="textSubtle">Prize pool</Text>
                <Display bold fontSize={['32px', null, '42px']} lineHeight="1.05" color="gold" data-testid="v3-prize" style={{ overflowWrap: 'anywhere', textShadow: '0 0 28px rgba(246,196,83,.45)' }}>
                  <TokenAmount raw={c.rewardAmount} token={c.rewardToken} />
                </Display>
                <Text fontSize="14px" color="textSubtle">Top <Num>{c.numberOfWinners}</Num> share it by score</Text>
              </Box>
            </PrizeBox>
            {c.phase === 'live' && (
              <EnterButton
                href={`#${TRADE_ANCHOR}`}
                onClick={(e) => {
                  if (goToTrade()) e.preventDefault()
                }}
                data-testid="v3-primary-cta"
              >
                <Icon name="trend" size={20} /> {enterLabel(c.type, title)}
              </EnterButton>
            )}
            <Box data-testid="v3-share-row">
              <CompetitionShare
                inline
                tone={c.phase === 'live' ? 'ghost' : 'gold'}
                label="Share to invite traders"
                facts={{
                  id: c.id,
                  prize: tokenAmountText(c.rewardAmount, rewardMeta),
                  token: title,
                  pair: pair.symbol0 && pair.symbol1 ? `${pair.symbol0}/${pair.symbol1}` : undefined,
                  dex: pair.dex?.name,
                  startTime: c.startTime,
                  endTime: c.endTime,
                  winners: c.numberOfWinners,
                }}
              />
            </Box>
          </Box>
        </Head>
        {/* the elapsed bar means something only while the competition runs; an empty or full bar is decoration */}
        {c.phase === 'live' && <Bar $pct={elapsed} $tone="success" style={{ marginTop: 16 }} data-testid="v3-progress" />}
        {mine && !mine.claimed && (
          <Box mt="16px">
            <ActionButton disabled={!ready || pending} data-testid="v3-claim"
              onClick={async () => (await send('Claim reward', { address: contractAddress, abi: competitionV3Abi, functionName: 'withdrawRewardForIds', args: [[BigInt(c.id)]] })) && mutate()}>
              Claim <TokenAmount raw={mine.amount} token={c.rewardToken} />
            </ActionButton>
          </Box>
        )}
        {mine?.claimed && <Text mt="12px" color="success" bold>✓ Reward claimed</Text>}
        {c.phase === 'refundable' && (
          <Box mt="16px">
            <ActionButton variant="secondary" disabled={!ready || pending} data-testid="v3-refund"
              onClick={async () => (await send('Refund to creator', { address: contractAddress, abi: competitionV3Abi, functionName: 'refundCompetition', args: [BigInt(c.id)] })) && mutate()}>
              Return prize to creator
            </ActionButton>
          </Box>
        )}
      </Hero>

      <Night data-theme="dark">
        <Leaderboard
          rows={boardRows}
          title={lb?.kind === 'final' ? 'Final leaderboard' : lb?.kind === 'candidate' ? 'Result' : 'Leaderboard'}
          status={<LiveStatus kind={lb?.kind} live={lb?.live} ended={c.phase === 'awaiting_result'} />}
          phase={c.phase}
          kind={lb?.kind}
          rewardToken={c.rewardToken}
          rewardAmount={c.rewardAmount}
          winners={c.numberOfWinners}
          projectToken={c.projectToken}
          type={c.type}
          me={me}
          profiles={profiles}
          startTime={c.startTime}
          endTime={c.endTime}
          publishedAt={lb?.publishedAt}
          priceUsd={overview.data?.market?.priceUsd}
          empty={c.phase === 'upcoming' ? 'The leaderboard opens when the competition starts.' : 'No eligible trades yet.'}
        />
        <Trading data-testid="v3-trading" data-layout={side ? 'side' : 'stack'} data-panel={panel} $side={side}>
          <div className="chart">
            {overview.data ? (
              <SectionBoundary name="chart"><ChartCard o={overview.data} pool={c.pool} onPool={() => undefined} tf={tf} onTf={setUserTf} marks={chartMarks} hidePicker /></SectionBoundary>
            ) : overview.error ? (
              <Panel data-testid="v3-chart-unavailable">
                <Text bold>The price chart is not available right now</Text>
                <Text fontSize="14px" color="textSubtle" mt="4px">The market data source did not answer. Trading below still works: it reads the pool from the chain.</Text>
              </Panel>
            ) : (
              <Skeleton height="360px" />
            )}
          </div>
          <div className="trade" id={TRADE_ANCHOR} style={{ scrollMarginTop: 72 }}>
            {c.phase === 'live' ? (
              <Box style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
                <SectionBoundary name="trade">
                  <PoolTradePanel
                    pool={c.pool}
                    projectToken={c.projectToken}
                    liquidityUsd={selPool?.liquidityUsd ?? null}
                    competition={{ id: c.id, rulesHref: '#rules' }}
                    gate={ack.state === 'done' ? null : <RulesAck state={ack.state} onSign={ack.sign} error={ack.error} />}
                  />
                </SectionBoundary>
                <Panel>{statusBlock}</Panel>
              </Box>
            ) : (
              <Panel data-testid="v3-trade-closed">
                <Display bold fontSize="20px">Trade in this competition</Display>
                {c.phase === 'upcoming' ? (
                  <>
                    <Text bold mt="8px" data-testid="v3-opens-at">Opens at {whenBoth(c.startTime).utc}</Text>
                    <Text fontSize="14px" color="textSubtle">{whenBoth(c.startTime).local}. The trade panel appears here when the competition starts. Only trades through the pool between the start and the end count.</Text>
                  </>
                ) : (
                  <>
                    <Text bold mt="8px" data-testid="v3-ended">Competition ended</Text>
                    <Text fontSize="14px" color="textSubtle">Trades made after {whenBoth(c.endTime).utc} do not count. The leaderboard above is the result.</Text>
                  </>
                )}
                <Hairline>{statusBlock}</Hairline>
                <Hairline data-testid="v3-trade-timeline">
                  <Text fontSize="13px" color="textSubtle" mb="6px">Timeline</Text>
                  <Box style={{ display: 'grid', gap: 2 }}>
                    {marks.map((m) => (
                      <Step key={m.label} $done={m.done} $now={false}>
                        <Flex style={{ gap: 8 }} flexWrap="wrap" alignItems="baseline">
                          <Text bold fontSize="14px">{m.label}</Text>
                          <Text fontSize="13px" color="textSubtle"><Num>{fmtDate(m.at)}</Num></Text>
                        </Flex>
                      </Step>
                    ))}
                  </Box>
                </Hairline>
              </Panel>
            )}
          </div>
          <div className="history">
            <SectionBoundary name="history">
              <TradeHistory token={c.projectToken} symbol={project.symbol ?? 'Token'} fixedPool={c.pool} window={{ start: c.startTime, end: c.endTime }} title="Trades in this pool" />
            </SectionBoundary>
          </div>
          <div className="rules">
            {/* elements, never calls: RulesSummary owns hooks, and a call would add them to this component's render once `c` arrives */}
            {side ? <RulesSummary {...rulesProps} /> : <RulesCard {...rulesProps} />}
          </div>
        </Trading>

        <Box mt="16px">
          <Fold title="Details" testId="v3-more" hint="Timeline, liquidity lock, published result, addresses">
            <Steps data-testid="v3-timeline">
              {marks.map((m) => (
                <Step key={m.label} $done={m.done} $now={c.phase === 'live' && m.label === 'Ends'}>
                  <Box><Text bold fontSize="14px">{m.label}</Text><Text fontSize="13px" color="textSubtle"><Num>{fmtDate(m.at)}</Num></Text></Box>
                </Step>
              ))}
            </Steps>
            <Box mt="16px"><LockBadge lock={c.liquidityLock} pool={c.pool} pair={{ symbol0: pair.symbol0, symbol1: pair.symbol1, fee: pair.fee, dexName: pair.dex?.name }} /></Box>
            <Text fontSize="13px" mt="12px" color="textSubtle">{type?.help}</Text>
            <Box mt="16px" data-testid="v3-report">
              <Display bold fontSize="18px">Published result</Display>
              {!report && <Text fontSize="14px" color="textSubtle" mt="8px">Not published yet. The full result is published here before it is sent on-chain.</Text>}
              {report && (
                <>
                  <Text fontSize="13px" mt="8px">Result hash <Mono>{report.resultsHash}</Mono></Text>
                  {report.kind === 'candidate' ? (
                    <Text fontSize="13px" bold color="warning" mt="4px" data-testid="v3-report-candidate">Published before the settlement transaction; final once the contract stores this hash</Text>
                  ) : (
                    <Text fontSize="13px" bold color={report.verifiedOnChain ? 'success' : 'failure'} mt="4px">
                      {report.verifiedOnChain ? '✓ Matches the hash stored on-chain' : 'Does not match the chain'}
                    </Text>
                  )}
                  <Text fontSize="12px" color="textSubtle" mt="4px">{report.howToVerify}</Text>
                  <a href={`${apiUrl}/${c.id}/report`} target="_blank" rel="noreferrer">
                    <Button variant="text" px="0" style={{ minHeight: 44 }}>Download report (JSON)</Button>
                  </a>
                </>
              )}
            </Box>
            <Box mt="16px">
              <DetailsCard pool={c.pool} creator={c.creator} projectToken={c.projectToken} rewardToken={c.rewardToken} createdTx={c.createdTx} refundAfter={c.refundAfter} />
            </Box>
          </Fold>
        </Box>
      </Night>
    </Shell>
  )
}
